import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, LogOut } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Counter } from "@/lib/animations";

const TABS = ["Overview", "Orders", "Products", "Customers"];

const EMPTY_PRODUCT = {
  name: "", slug: "", category: "vegan-pu-leather",
  description: "Crafted with premium materials and contemporary tailoring.",
  price: 0, original_price: null, images: [], sizes: ["S","M","L","XL","XXL"],
  colors: ["Black"], stock: 50, is_featured: false, is_bestseller: false,
  fabric: "", care: "",
};

const ORDER_STATUSES = ["pending", "paid", "processing", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("Overview");

  return (
    <Layout hideFooter>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-[#C9A227]">Admin</div>
            <h1 className="mt-2 text-3xl md:text-4xl font-light text-[#163A2F] tracking-tight">Norbu Dashboard</h1>
            <div className="text-sm text-[#163A2F]/60 mt-1">Signed in as {user?.email}</div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-[#8C2A2A]"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>

        <div className="mt-8 border-b hairline flex flex-wrap gap-6">
          {TABS.map(t => (
            <button
              key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`pb-3 text-xs uppercase tracking-[0.2em] border-b-2 ${tab === t ? "border-[#C9A227] text-[#163A2F]" : "border-transparent text-[#163A2F]/50"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "Overview"  && <Overview />}
          {tab === "Orders"    && <Orders />}
          {tab === "Products"  && <Products />}
          {tab === "Customers" && <Customers />}
        </div>
      </div>
    </Layout>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-[#163A2F]/60 overline">Loading…</div>;
  const cards = [
    { label: "Revenue", value: stats.revenue, isMoney: true },
    { label: "Orders", value: stats.total_orders },
    { label: "Customers", value: stats.customers },
    { label: "Products", value: stats.products },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map((c, i) => (
        <div key={i} className="border hairline p-6 bg-white">
          <div className="overline text-[#C9A227]">{c.label}</div>
          <div className="mt-3 text-4xl font-extralight text-[#163A2F]">
            {c.isMoney ? `₹${new Intl.NumberFormat("en-IN").format(Math.round(c.value || 0))}` : <Counter to={c.value} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/orders").then(r => setOrders(r.data || []));
  useEffect(() => { load(); }, []);

  async function update(o, patch) {
    try {
      const { data } = await api.put(`/admin/orders/${o.id}`, { status: patch.status ?? o.status, tracking_number: patch.tracking ?? o.tracking_number, courier: patch.courier ?? o.courier });
      setOrders(prev => prev.map(p => p.id === data.id ? data : p));
      if (active?.id === data.id) setActive(data);
      toast.success("Order updated");
    } catch { toast.error("Update failed"); }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-[#163A2F]/60 border-b hairline">
          <tr>
            <th className="py-3 pr-4 overline">Order</th>
            <th className="py-3 pr-4 overline">Customer</th>
            <th className="py-3 pr-4 overline">Date</th>
            <th className="py-3 pr-4 overline">Status</th>
            <th className="py-3 pr-4 overline">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id} className="border-b hairline">
              <td className="py-4 pr-4 font-medium text-[#163A2F]">{o.order_number}</td>
              <td className="py-4 pr-4 text-[#163A2F]">{o.user_email}</td>
              <td className="py-4 pr-4 text-[#163A2F]/70">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
              <td className="py-4 pr-4">
                <select value={o.status} onChange={e => update(o, { status: e.target.value })} data-testid={`order-status-${o.order_number}`} className="border hairline px-2 py-1 bg-transparent">
                  {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="py-4 pr-4 text-[#163A2F]">{formatINR(o.total)}</td>
              <td className="py-4 text-right"><button onClick={() => setActive(o)} className="text-[#C9A227] underline text-xs">Details</button></td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-[#163A2F]/60">No orders yet.</td></tr>}
        </tbody>
      </table>

      {active && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-[#F8F6F0] max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div>
                <div className="overline text-[#C9A227]">Order</div>
                <div className="text-2xl font-light text-[#163A2F]">{active.order_number}</div>
              </div>
              <button onClick={() => setActive(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="my-5 gold-rule" />
            <div className="text-sm text-[#163A2F] space-y-1">
              <div>Status: <span className="uppercase tracking-widest">{active.status}</span></div>
              <div>Customer: {active.user_email}</div>
              <div>Payment: {active.payment_method}</div>
            </div>
            <div className="mt-5 space-y-3">
              {active.items.map((it, idx) => (
                <div key={idx} className="flex gap-3">
                  <img src={it.image} className="w-14 h-18 object-cover" alt="" />
                  <div className="text-sm flex-1">
                    <div>{it.name}</div>
                    <div className="text-[#163A2F]/60">{it.size} · {it.color || "Default"} · Qty {it.quantity}</div>
                  </div>
                  <div className="text-sm">{formatINR(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="overline text-[#163A2F]/70">Courier</label>
                <input defaultValue={active.courier || ""} onBlur={(e) => update(active, { courier: e.target.value })} className="mt-1 w-full px-3 py-2 border hairline bg-transparent" />
              </div>
              <div>
                <label className="overline text-[#163A2F]/70">Tracking #</label>
                <input defaultValue={active.tracking_number || ""} onBlur={(e) => update(active, { tracking: e.target.value })} className="mt-1 w-full px-3 py-2 border hairline bg-transparent" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products?limit=500").then(r => setProducts(r.data || []));
  useEffect(() => { load(); }, []);

  async function remove(p) {
    if (!confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  }

  async function save(form) {
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        original_price: form.original_price ? Number(form.original_price) : null,
        stock: Number(form.stock),
        images: typeof form.images === "string" ? form.images.split(/\n+/).map(s => s.trim()).filter(Boolean) : form.images,
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map(s => s.trim()).filter(Boolean) : form.sizes,
        colors: typeof form.colors === "string" ? form.colors.split(",").map(s => s.trim()).filter(Boolean) : form.colors,
      };
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post(`/products`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error("Save failed"); }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-[#163A2F]/70 text-sm">{products.length} products</div>
        <button onClick={() => setEditing({ ...EMPTY_PRODUCT })} data-testid="add-product" className="flex items-center gap-2 bg-[#163A2F] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em]">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {products.map(p => (
          <div key={p.id} className="border hairline p-3 bg-white">
            <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[4/5] object-cover bg-[#163A2F]/5" />
            <div className="mt-3 text-[#163A2F] text-sm font-medium leading-snug">{p.name}</div>
            <div className="text-xs text-[#163A2F]/60">{p.category}</div>
            <div className="mt-2 flex justify-between items-center">
              <div className="text-[#163A2F] font-medium">{formatINR(p.price)}</div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...p, images: (p.images || []).join("\n"), sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", ") })} className="text-[#163A2F]/70 hover:text-[#163A2F]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p)} className="text-[#8C2A2A]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ProductForm({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F8F6F0] max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <div className="text-xl font-light text-[#163A2F]">{f.id ? "Edit product" : "New product"}</div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          {[
            { k:"name", l:"Name", span:2 },
            { k:"slug", l:"Slug (unique)" },
            { k:"category", l:"Category slug" },
            { k:"price", l:"Price (₹)", type:"number" },
            { k:"original_price", l:"Original price", type:"number" },
            { k:"stock", l:"Stock", type:"number" },
            { k:"fabric", l:"Fabric" },
            { k:"care", l:"Care", span:2 },
            { k:"description", l:"Description", span:2, multi:true },
            { k:"images", l:"Images (one URL per line)", span:2, multi:true },
            { k:"sizes", l:"Sizes (comma separated)" },
            { k:"colors", l:"Colors (comma separated)" },
          ].map(field => (
            <div key={field.k} className={field.span === 2 ? "sm:col-span-2" : ""}>
              <label className="overline text-[#163A2F]/70">{field.l}</label>
              {field.multi ? (
                <textarea rows={field.k === "images" ? 4 : 3} value={f[field.k] ?? ""} onChange={e => set(field.k, e.target.value)} className="mt-1 w-full px-3 py-2 border hairline bg-transparent" />
              ) : (
                <input type={field.type || "text"} value={f[field.k] ?? ""} onChange={e => set(field.k, field.type === "number" ? e.target.value : e.target.value)} className="mt-1 w-full px-3 py-2 border hairline bg-transparent" />
              )}
            </div>
          ))}
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_featured} onChange={e => set("is_featured", e.target.checked)} /> Featured</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_bestseller} onChange={e => set("is_bestseller", e.target.checked)} /> Bestseller</label>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 border hairline text-[#163A2F] text-xs uppercase tracking-[0.2em]">Cancel</button>
          <button onClick={() => onSave(f)} data-testid="save-product" className="px-5 py-2.5 bg-[#163A2F] text-white text-xs uppercase tracking-[0.2em]">Save</button>
        </div>
      </div>
    </div>
  );
}

function Customers() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/customers").then(r => setList(r.data || [])); }, []);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-[#163A2F]/60 border-b hairline">
          <tr><th className="py-3 overline">Name</th><th className="py-3 overline">Email</th><th className="py-3 overline">Joined</th></tr>
        </thead>
        <tbody>
          {list.map(c => (
            <tr key={c.id} className="border-b hairline">
              <td className="py-3 text-[#163A2F]">{c.name}</td>
              <td className="py-3 text-[#163A2F]/80">{c.email}</td>
              <td className="py-3 text-[#163A2F]/60">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-[#163A2F]/60">No customers yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
